import { getFullName, getInitials } from '@/shared/lib/names';
import type { Person } from '@/store/people/types';
import { Avatar, Badge, Divider, Spacer } from '@heroui/react';
import { StandardIcon } from '../icon';
import type { ReactNode } from 'react';

export function PersonItem({
  item,
  className,
  email = true,
  phone = true,
  icons,
  badge,
  nameStartContent,
  nameEndContent,
  onPress,
}: {
  item: Person;
  className?: string;
  email?: boolean;
  phone?: boolean;
  icons?: boolean;
  badge?: boolean;
  nameStartContent?: ReactNode;
  nameEndContent?: ReactNode;
  onPress?: () => void;
}) {
  const fullname = getFullName(item.data);
  const initials = getInitials(fullname);
  const Details = () => {
    const emailOutput = email ? item.data.email || '' : '';
    const phoneOutput = phone ? item.data.phone || '' : '';

    return (
      <div className='flex flex-col sm:flex-row'>
        {emailOutput && (
          <span className='mt-1 flex flex-row items-center sm:mt-0'>
            {icons && (
              <StandardIcon id='mdi:email-outline' size={18} className='mr-1' />
            )}
            {emailOutput}
          </span>
        )}
        {emailOutput && phoneOutput && !icons && (
          <Divider
            orientation='vertical'
            className='mx-2 hidden h-4 w-[1px] shadow-sm sm:block'
          />
        )}
        {emailOutput && phoneOutput && icons && (
          <Spacer className='hidden grow sm:block' />
        )}
        {phoneOutput && (
          <span className='mt-1 flex flex-row items-center sm:mt-0'>
            {icons && (
              <StandardIcon id='mdi:phone-outline' size={18} className='mr-1' />
            )}
            {phoneOutput}
          </span>
        )}
      </div>
    );
  };

  const AvatarContainer = ({ children }: { children: ReactNode }) => {
    return !badge || !item.data.uid ? (
      children
    ) : (
      <Badge
        isOneChar
        content={
          <StandardIcon id='mdi:check-circle' size={14} className='white' />
        }
        placement='bottom-right'
        size='sm'
        color='success'
      >
        {children}
      </Badge>
    );
  };

  return (
    <div
      className={`flex flex-row items-center gap-2 ${className || ''}`}
      onClick={onPress}
    >
      <AvatarContainer>
        <Avatar
          alt={fullname}
          name={initials}
          className='flex-shrink-0 self-start'
          color='primary'
          size='sm'
        />
      </AvatarContainer>
      <div className='flex w-full grow flex-col'>
        <span className='flex flex-row items-center gap-2 text-small'>
          {nameStartContent} {fullname} {nameEndContent}
        </span>
        <span className='flex flex-row gap-2 text-tiny text-default-500'>
          <Details />
        </span>
      </div>
    </div>
  );
}
